/**
 * Header Component
 */

import React, { useState } from 'react';
import { 
  FileSpreadsheet, 
  Download, 
  Save, 
  FolderOpen, 
  Menu,
  X,
  FileText,
  Calculator,
} from 'lucide-react';

export default function Header({ 
  scenarioName, 
  onScenarioNameChange, 
  onSave, 
  onExportExcel, 
  onExportPDF,
  onOpenScenarios,
}) {
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  
  return (
    <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo & Title */}
          <div className="flex items-center gap-3">
            <img
              src="/logo.svg"
              alt="SSP Calculator"
              className="w-9 h-9 md:w-11 md:h-11 flex-shrink-0"
            />
            <div className="min-w-0">
              <h1 className="text-base md:text-lg font-display font-semibold text-slate-900 leading-tight">
                SSP Modeller
              </h1>
              <p className="text-[10px] md:text-xs text-slate-500">UK Statutory Sick Pay</p>
            </div>
            
            {/* Scenario Name */}
            <div className="hidden sm:flex items-center gap-2 ml-4 pl-4 border-l border-slate-200">
              {isEditing ? (
                <input
                  type="text"
                  value={scenarioName}
                  onChange={(e) => onScenarioNameChange(e.target.value)}
                  onBlur={() => setIsEditing(false)}
                  onKeyDown={(e) => e.key === 'Enter' && setIsEditing(false)}
                  className="px-2 py-1 text-sm border border-primary-300 rounded focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500"
                  autoFocus
                />
              ) : (
                <button
                  onClick={() => setIsEditing(true)}
                  className="text-sm text-slate-600 hover:text-slate-900 px-2 py-1 rounded hover:bg-slate-100 transition"
                >
                  {scenarioName}
                </button>
              )}
            </div>
          </div>
          
          {/* Actions */}
          <div className="flex items-center gap-2">
            {/* Saved Scenarios */}
            <button
              onClick={onOpenScenarios}
              className="btn-ghost btn-sm"
              title="Saved Scenarios"
            >
              <FolderOpen className="w-4 h-4" />
              <span className="hidden sm:inline">Scenarios</span>
            </button>
            
            {/* Save */}
            <button
              onClick={onSave}
              className="btn-secondary btn-sm"
              title="Save Scenario"
            >
              <Save className="w-4 h-4" />
              <span className="hidden sm:inline">Save</span>
            </button>
            
            {/* Export Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowExportMenu(!showExportMenu)}
                className="btn-primary btn-sm"
              >
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">Export</span>
              </button>
              
              {showExportMenu && (
                <>
                  <div 
                    className="fixed inset-0 z-10" 
                    onClick={() => setShowExportMenu(false)} 
                  />
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-20 animate-fade-in">
                    <button
                      onClick={() => {
                        onExportExcel();
                        setShowExportMenu(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                    >
                      <FileSpreadsheet className="w-4 h-4 text-green-600" />
                      Export to Excel
                    </button>
                    <button
                      onClick={() => {
                        onExportPDF();
                        setShowExportMenu(false);
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                    >
                      <FileText className="w-4 h-4 text-red-600" />
                      Export to PDF
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* April 2026 Banner */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2">
          <p className="text-sm text-center font-medium">
            ⚠️ Employment Rights Act 2024: SSP changes from <strong>April 2026</strong> — Day 1 payment, no earnings threshold
          </p>
        </div>
      </div>
    </header>
  );
}
